import { useState, useRef, useEffect, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactionId } from '@big2/shared';
import { useI18n } from '../i18n.js';
import { useGameState } from '../net/GameProvider.js';
import { Icon } from './Icon.js';

export interface ReactionMeta {
  id: ReactionId;
  tier: 'free' | 'premium';
  emoji?: string;
  textMn: string;
  textEn: string;
  accentColor: string;
  bgGradient: string;
}

export const REACTIONS: ReactionMeta[] = [
  // ---- Free Simple Emoji Reactions ----
  {
    id: 'thumbs_up',
    tier: 'free',
    emoji: '👍',
    textMn: 'Сайн!',
    textEn: 'Nice!',
    accentColor: '#4ade80',
    bgGradient: 'rgba(0,0,0,0.75)',
  },
  {
    id: 'laugh',
    tier: 'free',
    emoji: '😂',
    textMn: 'Хаха',
    textEn: 'Haha',
    accentColor: '#fbbf24',
    bgGradient: 'rgba(0,0,0,0.75)',
  },
  {
    id: 'fire',
    tier: 'free',
    emoji: '🔥',
    textMn: 'Гал',
    textEn: 'Fire',
    accentColor: '#f97316',
    bgGradient: 'rgba(0,0,0,0.75)',
  },
  {
    id: 'coffee',
    tier: 'free',
    emoji: '☕',
    textMn: 'Цай',
    textEn: 'Coffee',
    accentColor: '#a8a29e',
    bgGradient: 'rgba(0,0,0,0.75)',
  },
  {
    id: 'clap',
    tier: 'free',
    emoji: '👏',
    textMn: 'Браво',
    textEn: 'Clap',
    accentColor: '#60a5fa',
    bgGradient: 'rgba(0,0,0,0.75)',
  },
  {
    id: 'sweat',
    tier: 'free',
    emoji: '😅',
    textMn: 'Ээ дээ',
    textEn: 'Oops',
    accentColor: '#38bdf8',
    bgGradient: 'rgba(0,0,0,0.75)',
  },

  // ---- Premium Illustrated Picture + Text Reactions ----
  {
    id: 'boss_deuce',
    tier: 'premium',
    textMn: 'БОСС ГАРЛАА!',
    textEn: 'LIKE A BOSS!',
    accentColor: '#fbbf24',
    bgGradient: 'linear-gradient(145deg, #3b0764 0%, #1e1b4b 60%, #451a03 100%)',
  },
  {
    id: 'airag_toast',
    tier: 'premium',
    textMn: 'САЙХАН ТОГЛОЛТ!',
    textEn: 'GG! CHEERS!',
    accentColor: '#38bdf8',
    bgGradient: 'linear-gradient(145deg, #0c4a6e 0%, #1e3a8a 60%, #172554 100%)',
  },
  {
    id: 'dragon_fire',
    tier: 'premium',
    textMn: 'ШАТААЛАА!',
    textEn: 'ON FIRE!',
    accentColor: '#f97316',
    bgGradient: 'linear-gradient(145deg, #7f1d1d 0%, #9a3412 60%, #431407 100%)',
  },
  {
    id: 'hurry_horse',
    tier: 'premium',
    textMn: 'ХУРДЛААРАЙ!',
    textEn: 'HURRY UP!',
    accentColor: '#4ade80',
    bgGradient: 'linear-gradient(145deg, #064e3b 0%, #115e59 60%, #042f2e 100%)',
  },
  {
    id: 'shock_boom',
    tier: 'premium',
    textMn: 'ЯМАР АЙМААР ЮМ БЭ!',
    textEn: 'NO WAY!!',
    accentColor: '#f43f5e',
    bgGradient: 'linear-gradient(145deg, #831843 0%, #4c0519 60%, #1e1b4b 100%)',
  },
  {
    id: 'cry_river',
    tier: 'premium',
    textMn: 'ДУУСЧИХЛАА...',
    textEn: 'RIP MY HAND...',
    accentColor: '#60a5fa',
    bgGradient: 'linear-gradient(145deg, #1e293b 0%, #0f172a 60%, #172554 100%)',
  },
];

export function renderPremiumIllustration(id: ReactionId, size = 64) {
  switch (id) {
    case 'boss_deuce':
      // Crowned 2 of Spades Boss Character with Cool Sunglasses & Sparkles
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
          {/* Glow */}
          <circle cx="32" cy="34" r="24" fill="rgba(251, 191, 36, 0.22)" />
          {/* Sparkles */}
          <path d="M10 16 L12 11 L14 16 L19 18 L14 20 L12 25 L10 20 L5 18 Z" fill="#fde047" />
          <path d="M52 12 L53.5 8 L55 12 L59 13.5 L55 15 L53.5 19 L52 15 L48 13.5 Z" fill="#fde047" />
          {/* Mini 2S Card Body */}
          <rect x="17" y="19" width="30" height="40" rx="5" fill="#ffffff" stroke="#fbbf24" strokeWidth="2.5" />
          <text x="21" y="29" fill="#0f172a" fontSize="9" fontWeight="900" fontFamily="system-ui">
            2♠
          </text>
          {/* Spade Emblem */}
          <path
            d="M32 39 C26 44, 24 48, 28 50 C30 51, 31 49, 31.5 48 L30 53 L34 53 L32.5 48 C33 49, 34 51, 36 50 C40 48, 38 44, 32 39 Z"
            fill="#0f172a"
          />
          {/* Cool Boss Sunglasses */}
          <rect x="19" y="30" width="11" height="7" rx="1.5" fill="#090d16" stroke="#fbbf24" strokeWidth="1.2" />
          <rect x="34" y="30" width="11" height="7" rx="1.5" fill="#090d16" stroke="#fbbf24" strokeWidth="1.2" />
          <line x1="30" y1="33" x2="34" y2="33" stroke="#fbbf24" strokeWidth="1.8" />
          <line x1="21" y1="32" x2="25" y2="35" stroke="#38bdf8" strokeWidth="1.4" />
          <line x1="36" y1="32" x2="40" y2="35" stroke="#38bdf8" strokeWidth="1.4" />
          {/* Golden Crown on Top */}
          <path
            d="M17 20 L19 8 L26 14 L32 5 L38 14 L45 8 L47 20 Z"
            fill="#fbbf24"
            stroke="#f59e0b"
            strokeWidth="1.5"
          />
          <circle cx="32" cy="14" r="2" fill="#ef4444" />
          <circle cx="24" cy="16" r="1.5" fill="#38bdf8" />
          <circle cx="40" cy="16" r="1.5" fill="#38bdf8" />
        </svg>
      );

    case 'airag_toast':
      // Traditional Mongolian Blue & Gold Bowl (Хул) with Khatag & Stars
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="34" r="24" fill="rgba(56, 189, 248, 0.22)" />
          {/* Radiant Stars */}
          <circle cx="20" cy="14" r="2" fill="#fde047" />
          <circle cx="32" cy="10" r="2.5" fill="#fde047" />
          <circle cx="44" cy="14" r="2" fill="#fde047" />
          {/* Blue Khatag Silk Scarf Drape */}
          <path
            d="M8 38 C14 32, 22 48, 32 48 C42 48, 50 32, 56 38 C53 45, 44 54, 32 54 C20 54, 11 45, 8 38 Z"
            fill="#0284c7"
            stroke="#38bdf8"
            strokeWidth="1.5"
          />
          {/* White Frothy Airag Top */}
          <ellipse cx="32" cy="26" rx="18" ry="5" fill="#f8fafc" stroke="#bae6fd" strokeWidth="1.5" />
          {/* Golden-Trimmed Bowl (Хул) */}
          <path
            d="M14 26 C14 40, 22 46, 32 46 C42 46, 50 40, 50 26 Z"
            fill="#1e3a8a"
            stroke="#fbbf24"
            strokeWidth="2.2"
          />
          {/* Golden Meander / Ulzii Band on Bowl */}
          <line x1="16" y1="32" x2="48" y2="32" stroke="#fbbf24" strokeWidth="2" strokeDasharray="4 2" />
          {/* Bowl Base */}
          <rect x="24" y="45" width="16" height="4" rx="2" fill="#fbbf24" />
        </svg>
      );

    case 'dragon_fire':
      // Blazing Inferno / Fire Emblem with Sizzling Card
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="34" r="24" fill="rgba(249, 115, 22, 0.28)" />
          {/* Outer Red/Orange Flame */}
          <path
            d="M32 6 C22 18, 12 24, 14 40 C16 52, 24 58, 32 58 C40 58, 48 52, 50 40 C52 26, 42 22, 42 12 C38 18, 35 16, 32 6 Z"
            fill="#ea580c"
            stroke="#fde047"
            strokeWidth="1.5"
          />
          {/* Inner Golden Flame */}
          <path
            d="M32 18 C26 26, 20 32, 22 42 C23 50, 27 54, 32 54 C37 54, 41 50, 42 42 C43 32, 37 26, 32 18 Z"
            fill="#facc15"
          />
          {/* White-Hot Core */}
          <path d="M32 30 C28 36, 27 42, 32 50 C37 42, 36 36, 32 30 Z" fill="#fef08a" />
          {/* Fierce Eyes */}
          <polygon points="22,36 29,39 24,41" fill="#450a0a" />
          <polygon points="42,36 35,39 40,41" fill="#450a0a" />
        </svg>
      );

    case 'hurry_horse':
      // Turbo Stopwatch + Lightning Speed Lines
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
          <circle cx="34" cy="36" r="22" fill="rgba(74, 222, 128, 0.2)" />
          {/* Speed Lines */}
          <line x1="4" y1="26" x2="16" y2="26" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="2" y1="36" x2="14" y2="36" stroke="#fde047" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="6" y1="46" x2="17" y2="46" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" />
          {/* Stopwatch Top Button */}
          <rect x="31" y="9" width="8" height="5" rx="1.5" fill="#fbbf24" />
          <path d="M46 15 L51 20" stroke="#fbbf24" strokeWidth="3" strokeLinecap="round" />
          {/* Stopwatch Face */}
          <circle cx="35" cy="36" r="19" fill="#0f172a" stroke="#4ade80" strokeWidth="3" />
          <circle cx="35" cy="36" r="15" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeDasharray="3 3" />
          {/* Clock Hands */}
          <line x1="35" y1="36" x2="35" y2="23" stroke="#f87171" strokeWidth="2.8" strokeLinecap="round" />
          <line x1="35" y1="36" x2="45" y2="36" stroke="#fde047" strokeWidth="2.8" strokeLinecap="round" />
          <circle cx="35" cy="36" r="3" fill="#ffffff" />
        </svg>
      );

    case 'shock_boom':
      // Comic Starburst Explosion + Electric Lightning Bolt
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
          {/* Starburst Polygon */}
          <polygon
            points="32,4 39,17 54,10 47,25 60,32 47,39 54,54 39,47 32,60 25,47 10,54 17,39 4,32 17,25 10,10 25,17"
            fill="#f43f5e"
            stroke="#fde047"
            strokeWidth="2"
          />
          <polygon
            points="32,12 37,22 48,17 43,27 52,32 43,37 48,47 37,42 32,52 27,42 16,47 21,37 12,32 21,27 16,17 27,22"
            fill="#fb923c"
          />
          {/* Central Electric Bolt */}
          <polygon
            points="35,14 23,33 32,33 28,50 42,29 33,29"
            fill="#fef08a"
            stroke="#0f172a"
            strokeWidth="1.5"
          />
        </svg>
      );

    case 'cry_river':
    default:
      // Dramatic Crying Face with Waterfall Tears & Tiny 3♦ Card
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="30" r="20" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" />
          {/* Waterfall Tear Streams */}
          <path d="M19 30 C17 40, 15 50, 16 58 L25 58 C25 50, 24 40, 24 30 Z" fill="#38bdf8" />
          <path d="M40 30 C40 40, 39 50, 39 58 L48 58 C49 50, 47 40, 45 30 Z" fill="#38bdf8" />
          {/* Tear puddle */}
          <ellipse cx="32" cy="58" rx="22" ry="4" fill="#0284c7" />
          {/* Crying Eyes */}
          <path d="M17 27 Q21.5 23 26 27" stroke="#451a03" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M38 27 Q42.5 23 47 27" stroke="#451a03" strokeWidth="2.5" strokeLinecap="round" />
          {/* Wailing Mouth */}
          <path d="M25 39 C27 34, 37 34, 39 39 C39 43, 25 43, 25 39 Z" fill="#7f1d1d" stroke="#451a03" strokeWidth="1.5" />
        </svg>
      );
  }
}

export function ReactionBubble({
  reactionId,
  seq,
  playerName,
}: {
  reactionId: ReactionId;
  seq: number;
  playerName?: string;
}) {
  const { lang } = useI18n();
  const meta = REACTIONS.find((r) => r.id === reactionId);
  if (!meta) return null;

  if (meta.tier === 'free') {
    return (
      <div
        key={`${reactionId}-${seq}`}
        className="reaction-animate"
        style={{
          background: 'rgba(15, 23, 42, 0.95)',
          border: `2px solid ${meta.accentColor}`,
          borderRadius: 999,
          padding: '6px 14px',
          fontSize: '2.2em',
          lineHeight: 1,
          boxShadow: '0 10px 30px rgba(0,0,0,0.65), 0 0 16px rgba(251, 191, 36, 0.35)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          zIndex: 80,
          pointerEvents: 'none',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <span>{meta.emoji}</span>
      </div>
    );
  }

  // Premium Sticker + Text Bubble
  const label = lang === 'mn' ? meta.textMn : meta.textEn;
  return (
    <div
      key={`${reactionId}-${seq}`}
      className="reaction-animate"
      style={{
        background: meta.bgGradient,
        border: `2px solid ${meta.accentColor}`,
        borderRadius: 14,
        padding: '8px 14px 10px',
        boxShadow: `0 12px 32px rgba(0,0,0,0.7), 0 0 16px ${meta.accentColor}55`,
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        zIndex: 80,
        minWidth: 120,
        pointerEvents: 'none',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {playerName && (
        <div style={{ fontSize: '0.68em', opacity: 0.8, fontWeight: 600, color: '#e2e8f0' }}>{playerName}</div>
      )}
      {renderPremiumIllustration(reactionId, 58)}
      <div
        style={{
          background: meta.accentColor,
          color: '#090d16',
          fontWeight: 900,
          fontSize: '0.78em',
          letterSpacing: 0.5,
          padding: '3px 10px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function ReactionPicker({
  onSelect,
  onOpenProfile,
  direction = 'up',
  compact = false,
}: {
  onSelect: (id: ReactionId) => void;
  onOpenProfile?: () => void;
  direction?: 'up' | 'down';
  compact?: boolean;
}) {
  const { lang } = useI18n();
  const { state } = useGameState();
  const isMn = lang === 'mn';
  const isPaidTier = state.profile?.tier === 'paid';
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const popupId = useId();

  const closePicker = () => {
    setOpen(false);
    setPosition(null);
    triggerRef.current?.focus({ preventScroll: true });
  };

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const popup = popupRef.current;
      if (!trigger || !popup) return;
      const width = Math.min(304, window.innerWidth - 16);
      const height = popup.getBoundingClientRect().height || 300;
      const gap = 8;
      const above = trigger.top - gap;
      const below = window.innerHeight - trigger.bottom - gap;
      const useBelow = direction === 'down' ? below >= height || below >= above : above < height && below > above;
      const top = Math.max(8, Math.min(window.innerHeight - height - 8,
        useBelow ? trigger.bottom + gap : trigger.top - height - gap));
      const left = Math.max(8, Math.min(window.innerWidth - width - 8, trigger.left + trigger.width / 2 - width / 2));
      setPosition(current => current?.top === top && current.left === left && current.width === width ? current : { top, left, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, direction]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePicker();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      popupRef.current?.querySelector<HTMLButtonElement>('.reaction-free-grid button')?.focus({ preventScroll: true });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !popupRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handleOutside);
    window.addEventListener('touchstart', handleOutside);
    return () => {
      window.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const freeReactions = REACTIONS.filter((r) => r.tier === 'free');
  const premiumReactions = REACTIONS.filter((r) => r.tier === 'premium');

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { setPosition(null); setOpen((o) => !o); }}
        className={compact ? 'icon-button' : undefined}
        aria-label={isMn ? 'Эмоци' : 'Reactions'}
        title={isMn ? 'Эмоци' : 'Reactions'}
        data-tooltip={compact && !open ? (isMn ? 'Эмоци' : 'Reactions') : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? popupId : undefined}
        style={compact ? undefined : {
          background: open ? '#fbbf24' : 'rgba(255,255,255,0.16)',
          color: open ? '#000' : '#fff',
          border: '1px solid rgba(255,255,255,0.28)',
          padding: '6px 14px',
          borderRadius: 8,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: '0.92em',
        }}
      >
        <Icon name="smile" />
        {!compact && <span>{isMn ? 'Эмоци' : 'React'}</span>}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
            ref={popupRef}
            id={popupId}
            role="dialog"
            aria-label={isMn ? 'Эмоци' : 'Reactions'}
            className="reaction-popover"
            style={{
              position: 'fixed',
              zIndex: 300,
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              width: position?.width ?? 304,
              visibility: position ? 'visible' : 'hidden',
              maxHeight: 'min(320px, calc(100dvh - 16px))',
              background: 'linear-gradient(165deg, #0b2e23 0%, #071c15 100%)',
              border: '1px solid rgba(251, 191, 36, 0.45)',
              borderRadius: 12,
              padding: '10px 12px',
              boxShadow: '0 14px 36px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              overflowY: 'auto',
              color: '#f6eddc',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                  paddingBottom: 6,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <span style={{ fontWeight: 800, fontSize: '0.92rem', color: '#fde68a', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>✨</span>
                <span>{isMn ? 'Эмоци сонгох' : 'Select Reaction'}</span>
              </span>
              <button
                type="button"
                onClick={closePicker}
                aria-label={isMn ? 'Хаах' : 'Close'}
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: 6,
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                ✕
              </button>
            </div>

            {/* Free Tier Section */}
            <div>
              <div
                style={{
                  fontSize: '0.72em',
                  fontWeight: 800,
                  opacity: 0.75,
                  marginBottom: 6,
                  letterSpacing: 0.6,
                }}
              >
                {isMn ? 'ҮНЭГҮЙ ЭМОЦИ (FREE)' : 'FREE REACTIONS'}
              </div>
              <div
                className="reaction-free-grid"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}
              >
                {freeReactions.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      onSelect(r.id);
                      closePicker();
                    }}
                    title={isMn ? r.textMn : r.textEn}
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(255,255,255,0.14)',
                      borderRadius: 8,
                      padding: '6px 0',
                      fontSize: '1.4em',
                      lineHeight: 1,
                      cursor: 'pointer',
                      transition: 'transform 0.1s ease',
                    }}
                  >
                    {r.emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Premium Tier Section */}
            <div>
              <div
                style={{
                  fontSize: '0.72em',
                  fontWeight: 800,
                  color: '#fde047',
                  marginBottom: 6,
                  letterSpacing: 0.6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>
                  {isPaidTier ? '👑 ' : '🔒 '}
                  {isMn ? 'ТӨЛБӨРТЭЙ СТИКЕР (PAID VIP)' : 'PAID TIER STICKERS'}
                </span>
                {!isPaidTier && state.profile && (
                  <button
                    type="button"
                    onClick={() => { closePicker(); onOpenProfile?.(); }}
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                      color: '#000',
                      fontWeight: 800,
                      fontSize: '0.95em',
                      padding: '2px 8px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    👑 {isMn ? 'VIP авах' : 'Upgrade'}
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
                {premiumReactions.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      onSelect(r.id);
                      closePicker();
                    }}
                    style={{
                      background: r.bgGradient,
                      border: `1.5px solid ${r.accentColor}`,
                      borderRadius: 10,
                      padding: '5px 3px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      color: '#fff',
                      position: 'relative',
                      opacity: isPaidTier ? 1 : 0.72,
                      cursor: 'pointer',
                    }}
                  >
                    {!isPaidTier && (
                      <span
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          fontSize: '0.68em',
                          background: 'rgba(0,0,0,0.75)',
                          color: '#fde68a',
                          padding: '1px 5px',
                          borderRadius: 99,
                          border: '1px solid rgba(251, 191, 36, 0.5)',
                        }}
                      >
                        🔒 VIP
                      </span>
                    )}
                    {renderPremiumIllustration(r.id, 32)}
                    <span
                      style={{
                        fontSize: '0.6em',
                        fontWeight: 800,
                        background: r.accentColor,
                        color: '#090d16',
                        padding: '2px 7px',
                        borderRadius: 999,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isMn ? r.textMn : r.textEn}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
      , document.body)}
    </div>
  );
}
